import type { TeacherRole } from '@kimuno/api-sdk/lib/types/responses/Text';
import { List } from '@mui/material';
import config from 'config';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { RoleSelected } from '../../../ampli';
import OnboardingStepRolesImage from '../../../assets/onboarding/onboarding-step-roles.svg';
import { spaces } from '../../../assets/styles/themes/tokens';
import { roleAvatarMap } from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import useAction from '../../../utils/useAction';
import OnboardingOptionItem from '../OnboardingOptionItem';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	skipAttemptsIncrement?: boolean;
	hideImage?: boolean;
	compact?: boolean;
}

const ONBOARDING_MAX_ATTEMPTS = config.onboarding.maxAttempts;

const OnboardingStepRoles: React.FC<OwnProps> = ({
	setOnboardingStep,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	skipAttemptsIncrement,
	hideImage,
	compact,
}) => {
	const user = useSelector(selectUser);
	const updateMe = useAction(updateMeAction);
	const [selectedRoles, setSelectedRoles] = React.useState<string[]>(user?.teacherRoles ?? []);
	const [isMaxSelected, setIsMaxSelected] = React.useState((user?.teacherRoles ?? []).length >= 3);
	const [fetchedRoles, setFetchedRoles] = React.useState<TeacherRole[]>([]);
	const { t } = useTranslation();

	useEffect(() => {
		if (user?.teacherRoles) {
			setSelectedRoles(user.teacherRoles);
			setIsMaxSelected(user.teacherRoles.length >= 3);
		}
	}, [user?.teacherRoles]);

	const contentHeights = {
		default: 'calc(100vh - 469px)',
		smallScreen: 'calc(100vh - 329px)',
	};

	useEffect(() => {
		services.text
			.fetchTeacherRoles()
			.then((r) => {
				setFetchedRoles(r);
			})
			.catch((error) => {
				notifyBugsnagHandledError(error);
			});
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (skipAttemptsIncrement) return;
		if (!user) return;

		if (user.onboardingAttempts < ONBOARDING_MAX_ATTEMPTS) {
			updateMe({
				data: {
					onboarding: {
						attempts: user.onboardingAttempts + 1,
					},
				},
			});
		}
	}, [user && user.id]);

	const sendRoleSelectedEvent = () => {
		if (user) {
			services.track.ampliEventTrack({
				event: new RoleSelected({
					role: selectedRoles,
				}),
				userProperties: {
					id: user.id,
					role: selectedRoles,
				},
			});
		}
	};

	const handleNext = async () => {
		if (selectedRoles.includes('Referendar:in')) {
			await updateMe({ isTrainee: true });
		}
		await updateMe({ teacherRoles: selectedRoles });
		sendRoleSelectedEvent();
		if (onNextOverride) {
			onNextOverride();
		} else {
			setOnboardingStep('schule');
		}
	};

	const handleClickOption = (role: TeacherRole) => {
		setSelectedRoles((prevSelectedRoles) => {
			const isSelected = prevSelectedRoles.includes(role.name);
			const updatedRoles = isSelected
				? prevSelectedRoles.filter((r) => r !== role.name)
				: [...prevSelectedRoles, role.name];

			setIsMaxSelected(updatedRoles.length >= 3);

			return updatedRoles;
		});
	};

	return (
		<>
			<OnboardingStepContainer
				title={t('What describes you best?')}
				subtitle={t('You can select one or more roles.')}
				stepNum={stepNum ?? 1}
				totalSteps={totalSteps}
				onBack={onBackOverride ?? undefined}
				onNext={handleNext}
				contentHeights={contentHeights}
				compact={compact}
			>
				<List
					dense
					sx={{
						width: '100%',
						paddingRight: spaces.xxs,
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.md,
					}}
				>
					{fetchedRoles.map((role) => (
						<OnboardingOptionItem
							key={role.name}
							label={role.name}
							avatar={roleAvatarMap[role.name as keyof typeof roleAvatarMap]}
							isSelected={selectedRoles.includes(role.name)}
							isDisabled={isMaxSelected && !selectedRoles.includes(role.name)}
							onClick={() => handleClickOption(role)}
						/>
					))}
				</List>
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepRolesImage />} />}
		</>
	);
};

export default OnboardingStepRoles;
